import { useState } from "react";
import { Link } from "react-router";
import { formatMoney } from "@metrickle/schema/currency";
import { PLAN_BY_ID } from "@metrickle/schema/plans";
import { useAiCredits, type AiBenchmark, type AiCredits } from "../../lib/admin";
import { AI_FEATURE_LABEL, fmtPeriod } from "../../lib/billing";
import { fmtNumber, fmtPercent } from "../../lib/format";
import { StatTile } from "../../components/stats";
import { Callout, Card, CardHeader, ErrorState, None, PageHeader, SelectField, Skeleton, StatGrid, Table, TableEmptyRow, TableScroll, TableSkeletonRows } from "../../components/ui";
import { usePageTitle } from "./components";

/** Pounds, to the penny; under a penny says so rather than showing £0.00. */
const fmtGbp = (gbp: number) => (gbp > 0 && gbp < 0.005 ? "under 1p" : formatMoney(Math.round(gbp * 100)));
const n = (v: number) => fmtNumber(v, { exact: true });

/**
 * AI credits across every workspace: what they cost at list price against what each plan charges,
 * and what active workspaces of a similar size or plan use, for sizing a customer's plan.
 */
export function AdminAiCredits() {
  usePageTitle("AI credits");
  const [period, setPeriod] = useState<string>();
  const q = useAiCredits(period);
  const d = q.data;

  if (q.isError) return <ErrorState error={q.error} />;

  const month = d ? fmtPeriod(d.period) : "";
  return (
    <>
      <PageHeader
        title="AI credits"
        subtitle="What each workspace's AI costs at list price, and what similar workspaces use. One credit is about 1p."
        actions={d && <SelectField aria-label="Month" items={d.periods.map((p) => ({ value: p, label: fmtPeriod(p) }))} value={d.period} onChange={setPeriod} />}
      />
      <div className="space-y-4" aria-busy={q.isFetching || undefined}>
        {d?.partial && (
          <Callout role="note">
            {month} isn't over, so these figures are partial. For benchmarks to quote, pick last month.
          </Callout>
        )}
        {d && d.totals.untracked > 0 && (
          <Callout role="note">
            {n(d.totals.untracked)} of {month}'s credits were used before Metrickle recorded what they were for, so they're in the totals but have no cost or feature.
          </Callout>
        )}

        <Card>
          <StatGrid loading={!d} cols={4}>
            {d && (
              <>
                <StatTile label="Credits used" value={n(d.totals.credits)} note={`${n(d.totals.calls)} AI calls`} />
                <StatTile label="AI cost" value={fmtGbp(d.totals.costGbp)} note="Claude API list price" />
                <StatTile label="Workspaces using AI" value={n(d.totals.workspacesUsingAi)} note={`of ${n(d.totals.activeWorkspaces)} active`} />
                <StatTile
                  label="Cost per workspace using AI"
                  value={d.totals.workspacesUsingAi ? fmtGbp(d.totals.costGbp / d.totals.workspacesUsingAi) : <None />}
                  note="Mean, this month"
                />
              </>
            )}
          </StatGrid>
        </Card>

        <Card>
          <CardHeader
            title="What similar workspaces use"
            subtitle="Credits a month across active workspaces (any events or AI that month), counting those that used none. Plan is today's plan."
          />
          <div className="grid @4xl:grid-cols-2">
            <BenchmarkTable caption={`AI credits in ${month} by monthly events, smallest first`} first="Monthly events" rows={d?.byScale} />
            <BenchmarkTable caption={`AI credits in ${month} by plan, Free first`} first="Plan" rows={d?.byPlan} allowance />
          </div>
        </Card>

        <div className="grid gap-4 @4xl:grid-cols-2">
          <Card className="min-w-0">
            <CardHeader title="By feature" subtitle={month} />
            <FeatureTable d={d} />
          </Card>
          <Card className="min-w-0">
            <CardHeader title="By month" subtitle="The last 12 months, across every workspace" />
            <MonthTable d={d} />
          </Card>
        </div>

        <Card>
          <CardHeader title="Workspaces" subtitle={`Active in ${month}, most credits first. Cost share is AI cost against the plan's list price.`} />
          <WorkspaceTable d={d} />
        </Card>
      </div>
    </>
  );
}

function BenchmarkTable({ caption, first, rows, allowance }: { caption: string; first: string; rows: AiBenchmark[] | undefined; allowance?: boolean }) {
  const columns = allowance ? 7 : 6;
  return (
    <TableScroll label={caption} divided>
      <Table caption={caption} className="min-w-[480px]">
        <thead>
          <tr>
            <th scope="col">{first}</th>
            <th scope="col" className="text-right">Workspaces</th>
            <th scope="col" className="text-right">Median</th>
            <th scope="col" className="text-right">90th percentile</th>
            <th scope="col" className="text-right">Most</th>
            <th scope="col" className="text-right">Median cost</th>
            {allowance && <th scope="col" className="text-right">Allowance</th>}
          </tr>
        </thead>
        <tbody>
          {!rows ? (
            <TableSkeletonRows rows={4} columns={columns} />
          ) : (
            rows.map((r) => (
              <tr key={r.key}>
                <th scope="row" className="font-medium text-ink">{r.label}</th>
                <td className="text-right text-ink-2">
                  {n(r.workspaces)}
                  {r.workspaces > 0 && <span className="block text-xs whitespace-nowrap text-ink-3">{n(r.usingAi)} using AI</span>}
                </td>
                {r.workspaces === 0 ? (
                  <td colSpan={allowance ? 5 : 4} className="text-right text-xs text-ink-3">No active workspaces</td>
                ) : (
                  <>
                    <td className="text-right font-medium text-ink">{n(r.median)}</td>
                    <td className="text-right text-ink">{n(r.p90)}</td>
                    <td className="text-right text-ink-2">{n(r.max)}</td>
                    <td className="text-right text-ink-2">{fmtGbp(r.medianCostGbp)}</td>
                    {allowance && <td className="text-right text-ink-3">{r.allowance === null ? "Custom" : n(r.allowance)}</td>}
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </TableScroll>
  );
}

const UNIT = { release_cause: "reading", ticket_sorting: "ticket" } as const;

function FeatureTable({ d }: { d: AiCredits | undefined }) {
  return (
    <TableScroll label="AI credits by feature" divided>
      <Table caption="AI calls, credits and cost by feature">
        <thead>
          <tr>
            <th scope="col">Feature</th>
            <th scope="col" className="text-right">Calls</th>
            <th scope="col" className="text-right">Credits</th>
            <th scope="col" className="text-right">Cost</th>
            <th scope="col" className="text-right">Cost per call</th>
          </tr>
        </thead>
        <tbody>
          {!d ? (
            <TableSkeletonRows rows={2} columns={5} />
          ) : (
            d.byFeature.map((f) => (
              <tr key={f.feature}>
                <th scope="row" className="font-medium text-ink">{AI_FEATURE_LABEL[f.feature]}</th>
                <td className="text-right text-ink-2">{n(f.calls)}</td>
                <td className="text-right font-medium text-ink">{n(f.credits)}</td>
                <td className="text-right text-ink-2">{fmtGbp(f.costGbp)}</td>
                <td className="text-right text-ink-2">
                  {f.calls ? (
                    <>
                      {fmtGbp(f.costGbp / f.calls)}
                      <span className="sr-only"> a {UNIT[f.feature]}</span>
                    </>
                  ) : (
                    <None />
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </TableScroll>
  );
}

function MonthTable({ d }: { d: AiCredits | undefined }) {
  // Leading months with nothing are before anyone used AI.
  const from = d ? d.months.findIndex((m) => m.credits > 0) : -1;
  const months = d && from >= 0 ? d.months.slice(from).reverse() : [];
  return (
    <TableScroll label="AI credits by month" divided>
      <Table caption="AI credits and cost by month, newest first">
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col" className="text-right">Workspaces</th>
            <th scope="col" className="text-right">Credits</th>
            <th scope="col" className="text-right">Cost</th>
          </tr>
        </thead>
        <tbody>
          {!d ? (
            <TableSkeletonRows rows={3} columns={4} />
          ) : !months.length ? (
            <TableEmptyRow columns={4}>No AI credits used in the last 12 months.</TableEmptyRow>
          ) : (
            months.map((m) => (
              <tr key={m.period}>
                <th scope="row" className="font-medium text-ink">{fmtPeriod(m.period)}</th>
                <td className="text-right text-ink-2">{n(m.workspaces)}</td>
                <td className="text-right font-medium text-ink">{n(m.credits)}</td>
                <td className="text-right text-ink-2">{fmtGbp(m.costGbp)}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </TableScroll>
  );
}

function WorkspaceTable({ d }: { d: AiCredits | undefined }) {
  return (
    <TableScroll label="AI credits by workspace" divided>
      <Table caption="Active workspaces by AI credits used, most first. Select a name to open the account." className="min-w-[960px]">
        <thead>
          <tr>
            <th scope="col">Workspace</th>
            <th scope="col" className="text-right">Events</th>
            <th scope="col" className="text-right">Credits</th>
            <th scope="col" className="text-right">Release causes</th>
            <th scope="col" className="text-right">Ticket sorting</th>
            <th scope="col" className="text-right">AI cost</th>
            <th scope="col" className="text-right">Plan price</th>
            <th scope="col" className="text-right">Cost share</th>
          </tr>
        </thead>
        <tbody>
          {!d ? (
            <TableSkeletonRows columns={8} />
          ) : !d.workspaces.length ? (
            <TableEmptyRow columns={8}>No active workspaces in {fmtPeriod(d.period)}.</TableEmptyRow>
          ) : (
            d.workspaces.map((w) => (
              <tr key={w.id}>
                <th scope="row">
                  <Link to={`/admin/accounts/${w.id}`} className="font-medium text-ink hover:underline">{w.name}</Link>
                  <span className="block text-xs text-ink-3">{PLAN_BY_ID[w.plan].name}</span>
                </th>
                <td className="text-right text-ink-2">{fmtNumber(w.events)}</td>
                <td className="text-right">
                  <span className="font-medium text-ink">{n(w.credits)}</span>
                  {w.allowance !== null && <span className="ml-1.5 text-xs text-ink-3">of {n(w.allowance)}</span>}
                </td>
                <td className="text-right text-ink-2">{n(w.byFeature.release_cause)}</td>
                <td className="text-right text-ink-2">{n(w.byFeature.ticket_sorting)}</td>
                <td className="text-right text-ink-2">
                  {fmtGbp(w.costGbp)}
                  {w.untracked > 0 && <span className="ml-1.5 text-xs text-ink-3">+{n(w.untracked)} untracked</span>}
                </td>
                <td className="text-right text-ink-2">
                  {w.listMonthlyGbp === null ? <None label="No set price" /> : formatMoney(w.listMonthlyGbp * 100)}
                  {w.paysMonthlyGbp !== null && w.listMonthlyGbp !== null && w.paysMonthlyGbp !== w.listMonthlyGbp && (
                    <span className="block text-xs text-ink-3">pays {formatMoney(Math.round(w.paysMonthlyGbp * 100))}</span>
                  )}
                </td>
                <td className="text-right text-ink-2">{w.listMonthlyGbp ? fmtPercent(w.costGbp / w.listMonthlyGbp) : <None />}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </TableScroll>
  );
}
